export interface GuideRect {
  left: number;
  right: number;
  top: number;
  bottom: number;
  width: number;
  height: number;
}

export type AlignmentGuideKind = 'surface' | 'grid' | 'sibling';

export interface AlignmentGuide {
  orientation: 'vertical' | 'horizontal';
  /** Screen-space position. The canvas overlay converts this to local CSS pixels. */
  position: number;
  kind: AlignmentGuideKind;
  /** Logical pixels. This is never a browser/screen-pixel value. */
  distance?: number;
  /** Center axes exist throughout a drag; only a threshold match is active. */
  active?: boolean;
}

export interface AlignmentGuideOptions {
  movingRect: GuideRect;
  surfaceRect: GuideRect;
  siblingRects?: GuideRect[];
  /** Optional screen-space grid positions in the same coordinate system as the rects. */
  gridLines?: number[];
  /** Raw screen-space pointer delta from the immutable drag start. */
  delta?: { x: number; y: number };
  /** Logical-pixel snap threshold. */
  threshold?: number;
  /** CSS logical pixels per screen pixel. */
  scale?: number;
}

export interface AlignmentSnap {
  target: number;
  anchor: 'left' | 'center' | 'right';
  kind: AlignmentGuideKind;
  /** Screen-space correction that is added to the raw pointer delta. */
  delta: number;
  /** Logical distance before the correction is applied. */
  distance: number;
}

export interface GuideResult {
  guides: AlignmentGuide[];
  /** Screen-space delta, preserving the existing drag caller contract. */
  dx: number;
  dy: number;
  /** Logical center coordinates relative to the editable surface. */
  centerX: number;
  centerY: number;
  snapX?: AlignmentSnap;
  snapY?: AlignmentSnap;
}

interface Candidate {
  target: number;
  anchor: 'left' | 'center' | 'right';
  kind: AlignmentGuideKind;
}

const DEFAULT_THRESHOLD = 8;

function distanceToLogicalPixels(value: number, scale: number): number {
  return Math.abs(value) / Math.max(scale, 0.01);
}

function nearestCandidate(
  anchors: Array<{ value: number; anchor: Candidate['anchor'] }>,
  candidates: Candidate[],
  thresholdScreen: number,
): { candidate: Candidate; distance: number; correction: number } | null {
  let best: { candidate: Candidate; distance: number; correction: number } | null = null;
  for (const anchor of anchors) {
    for (const candidate of candidates) {
      const correction = candidate.target - anchor.value;
      const distance = Math.abs(correction);
      if (distance > thresholdScreen) continue;
      if (!best || distance < best.distance) best = { candidate: { ...candidate, anchor: anchor.anchor }, distance, correction };
    }
  }
  return best;
}

function siblingCandidates(siblings: GuideRect[], axis: 'x' | 'y'): Candidate[] {
  return siblings.flatMap((item) => axis === 'x'
    ? [
      { target: item.left, anchor: 'left' as const, kind: 'sibling' as const },
      { target: item.right, anchor: 'right' as const, kind: 'sibling' as const },
      { target: item.left + item.width / 2, anchor: 'center' as const, kind: 'sibling' as const },
    ]
    : [
      { target: item.top, anchor: 'left' as const, kind: 'sibling' as const },
      { target: item.bottom, anchor: 'right' as const, kind: 'sibling' as const },
      { target: item.top + item.height / 2, anchor: 'center' as const, kind: 'sibling' as const },
    ]);
}

function gridCandidates(gridLines: number[]): Candidate[] {
  return gridLines.map((target) => ({ target, anchor: 'left' as const, kind: 'grid' as const }));
}

function guideForSnap(
  orientation: AlignmentGuide['orientation'],
  snap: AlignmentSnap,
): AlignmentGuide {
  return {
    orientation,
    position: snap.target,
    kind: snap.kind,
    distance: 0,
    active: true,
  };
}

/**
 * Calculates alignment guides in one shared screen-space context.
 *
 * Rects and raw deltas come from DOM pointer geometry. `scale` converts the
 * threshold and all displayed distances back to logical editor pixels. The
 * moving rect is never replaced with the snapped result, so every call is
 * still based on the immutable drag start plus the current raw pointer delta.
 */
export function calculateAlignmentGuides({
  movingRect,
  surfaceRect,
  siblingRects = [],
  gridLines = [],
  delta = { x: 0, y: 0 },
  threshold = DEFAULT_THRESHOLD,
  scale = 1,
}: AlignmentGuideOptions): GuideResult {
  const actualScale = Math.max(scale, 0.01);
  const thresholdScreen = threshold * actualScale;
  const moved = {
    left: movingRect.left + delta.x,
    right: movingRect.right + delta.x,
    top: movingRect.top + delta.y,
    bottom: movingRect.bottom + delta.y,
    width: movingRect.width,
    height: movingRect.height,
  };
  const surfaceCenterX = surfaceRect.left + surfaceRect.width / 2;
  const surfaceCenterY = surfaceRect.top + surfaceRect.height / 2;
  const movedCenterX = moved.left + moved.width / 2;
  const movedCenterY = moved.top + moved.height / 2;

  const xCenterMatch = nearestCandidate(
    [{ value: movedCenterX, anchor: 'center' }],
    [{ target: surfaceCenterX, anchor: 'center', kind: 'surface' }],
    thresholdScreen,
  );
  const yCenterMatch = nearestCandidate(
    [{ value: movedCenterY, anchor: 'center' }],
    [{ target: surfaceCenterY, anchor: 'center', kind: 'surface' }],
    thresholdScreen,
  );

  const xCandidates: Candidate[] = [
    { target: surfaceRect.left, anchor: 'left', kind: 'surface' },
    { target: surfaceRect.right, anchor: 'right', kind: 'surface' },
    ...gridCandidates(gridLines),
    ...siblingCandidates(siblingRects, 'x'),
  ];
  const yCandidates: Candidate[] = [
    { target: surfaceRect.top, anchor: 'left', kind: 'surface' },
    { target: surfaceRect.bottom, anchor: 'right', kind: 'surface' },
    ...siblingCandidates(siblingRects, 'y'),
  ];
  const xMatch = xCenterMatch ?? nearestCandidate(
    [
      { value: moved.left, anchor: 'left' },
      { value: moved.right, anchor: 'right' },
      { value: movedCenterX, anchor: 'center' },
    ],
    xCandidates,
    thresholdScreen,
  );
  const yMatch = yCenterMatch ?? nearestCandidate(
    [
      { value: moved.top, anchor: 'left' },
      { value: moved.bottom, anchor: 'right' },
      { value: movedCenterY, anchor: 'center' },
    ],
    yCandidates,
    thresholdScreen,
  );

  const snapX: AlignmentSnap | undefined = xMatch ? {
    target: xMatch.candidate.target,
    anchor: xMatch.candidate.anchor,
    kind: xMatch.candidate.kind,
    delta: xMatch.correction,
    distance: distanceToLogicalPixels(xMatch.distance, actualScale),
  } : undefined;
  const snapY: AlignmentSnap | undefined = yMatch ? {
    target: yMatch.candidate.target,
    anchor: yMatch.candidate.anchor,
    kind: yMatch.candidate.kind,
    delta: yMatch.correction,
    distance: distanceToLogicalPixels(yMatch.distance, actualScale),
  } : undefined;

  return {
    dx: delta.x + (snapX?.delta ?? 0),
    dy: delta.y + (snapY?.delta ?? 0),
    centerX: surfaceRect.width / actualScale / 2,
    centerY: surfaceRect.height / actualScale / 2,
    snapX,
    snapY,
    guides: [
      {
        orientation: 'vertical',
        position: surfaceCenterX,
        kind: 'surface',
        distance: xCenterMatch ? 0 : distanceToLogicalPixels(movedCenterX - surfaceCenterX, actualScale),
        active: Boolean(xCenterMatch),
      },
      ...(snapX && Math.abs(snapX.target - surfaceCenterX) > 0.01 ? [guideForSnap('vertical', snapX)] : []),
      {
        orientation: 'horizontal',
        position: surfaceCenterY,
        kind: 'surface',
        distance: yCenterMatch ? 0 : distanceToLogicalPixels(movedCenterY - surfaceCenterY, actualScale),
        active: Boolean(yCenterMatch),
      },
      ...(snapY && Math.abs(snapY.target - surfaceCenterY) > 0.01 ? [guideForSnap('horizontal', snapY)] : []),
    ],
  };
}
